import type { ApiError } from '@/api/client.types';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';

import styles from './GradesLoadFailed.module.css';

interface GradesLoadFailedProps {
  error: unknown;
  headingLevel?: 1 | 2;
  onRetry: () => void;
}

function describeFailure(error: unknown) {
  const kind =
    typeof error === 'object' && error !== null && 'kind' in error
      ? (error as ApiError).kind
      : undefined;

  switch (kind) {
    case 'network':
      return 'Kudu could not be reached. Check your connection, then try again.';
    case 'permission':
      return 'Only the course’s instructors can see these grades.';
    default:
      return 'No scores are shown rather than ones that may be wrong. Try again in a moment.';
  }
}

/**
 * A grades load that failed, said as such: never drawn as loaded, where the
 * roster's rows would read as zeros, nor as loading for ever. The error's
 * message is the backend's whole body and is never shown; the words come from
 * its kind.
 */
export function GradesLoadFailed({ error, headingLevel = 2, onRetry }: GradesLoadFailedProps) {
  const Heading = headingLevel === 1 ? 'h1' : 'h2';

  return (
    <Card className={styles.card} role="alert">
      <Heading className={styles.title}>The grades could not be loaded</Heading>
      <p className={styles.message}>{describeFailure(error)}</p>
      <Button className={styles.retry} onClick={onRetry} variant="secondary">
        Try again
      </Button>
    </Card>
  );
}
